import { BooleanInput } from "@angular/cdk/coercion";
import {
    AfterContentChecked,
    AfterContentInit,
    booleanAttribute,
    ChangeDetectorRef,
    Component,
    computed,
    contentChild,
    contentChildren,
    effect,
    ElementRef,
    inject,
    InjectionToken,
    input,
    isDevMode,
    viewChild,
    ViewEncapsulation
} from "@angular/core";
import { AbstractControlDirective } from "@angular/forms";
import { VdsIconComponent } from "@dcc/vestas-design-system/angular/components/icon";
import { PolymorpheusContent, PolymorpheusOutlet } from "@taiga-ui/polymorpheus";
import { VdsErrorComponent } from "./components/error.component";
import { VdsHintComponent } from "./components/hint.component";
import { VdsLabelComponent } from "./components/label.component";
import { VdsFormFieldControl } from "./form-field-control";

export type VdsFormFieldAppearance = "default" | "ai";

export const VDS_FORM_FIELD = new InjectionToken<VdsFormFieldComponent>("VdsFormField");

@Component({
    selector: "vds-form-field",
    exportAs: "vdsFormField",
    templateUrl: "./form-field.component.html",
    styleUrl: "./form-field.component.css",
    encapsulation: ViewEncapsulation.None,
    host: {
        class: "vds-form-field",
        "[class.vds-form-field--default]": "appearance() === 'default'",
        "[class.vds-form-field--ai]": "appearance() === 'ai'",
        "[class.vds-form-field--has-prefix]": "hasLeadingIcon()",
        "[class.vds-form-field--has-suffix]": "hasTrailingIconComputed()",
        "[class.vds-form-field--invalid]": "formFieldControl()?.errorState()",
        "[class.ng-untouched]": '_shouldForward("untouched")',
        "[class.ng-touched]": '_shouldForward("touched")',
        "[class.ng-pristine]": '_shouldForward("pristine")',
        "[class.ng-dirty]": '_shouldForward("dirty")',
        "[class.ng-valid]": '_shouldForward("valid")',
        "[class.ng-invalid]": '_shouldForward("invalid")',
        "[class.ng-pending]": '_shouldForward("pending")'
    },
    imports: [VdsIconComponent, PolymorpheusOutlet],
    providers: [{ provide: VDS_FORM_FIELD, useExisting: VdsFormFieldComponent }]
})
export class VdsFormFieldComponent implements AfterContentInit, AfterContentChecked {
    public elementRef = inject(ElementRef);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    textField = viewChild("textField", { read: ElementRef<HTMLElement> });
    label = contentChild(VdsLabelComponent, { read: VdsLabelComponent });

    formFieldControl = contentChild(VdsFormFieldControl, { read: VdsFormFieldControl });
    hintChildren = contentChildren(VdsHintComponent, { descendants: true });
    errorChildren = contentChildren(VdsErrorComponent, { descendants: true });

    readonly appearance = input<VdsFormFieldAppearance>("default");

    optionalLabel = input<string>("");

    clearable = input<boolean, BooleanInput>(false, { transform: booleanAttribute });
    clearLabel = input("Clear input");

    leadingIcon = input<PolymorpheusContent<never> | null>(null);
    trailingIcon = input<PolymorpheusContent<never> | null>(null);

    hasLeadingIcon = computed(() => !!this.leadingIcon());

    // Computed property to determine which trailing icon to show
    effectiveTrailingIcon = computed((): PolymorpheusContent<never> | null => {
        // If there's an error state, show error icon regardless of existing trailing icon
        if (this.formFieldControl()?.errorState()) {
            return "error-filled";
        }
        // Otherwise, show the normal trailing icon (can be null/undefined)
        return this.trailingIcon();
    });

    // Check if we should show any trailing icon (error or normal)
    hasTrailingIconComputed = computed(() => !!this.effectiveTrailingIcon());

    hasMessages = computed(() => {
        return this.errorChildren().length > 0 || this.hintChildren().length > 0;
    });

    displayedMessages = computed(() => {
        return this.errorChildren().length > 0 ? "error" : "hint";
    });

    ngAfterContentInit() {
        this.assertFormFieldControl();
    }

    ngAfterContentChecked() {
        this.assertFormFieldControl();
    }

    getConnectedOverlayOrigin(): ElementRef {
        return this.textField() || this?.elementRef;
    }

    showClearButton(): boolean {
        const control = this.formFieldControl();

        return !!this.clearable() && !!control?.clear && !control.empty && !control.disabledState() && !control.isReadonly();
    }

    clear(event: MouseEvent): void {
        event.stopPropagation();
        this.formFieldControl()?.clear?.();
    }

    _shouldForward(prop: keyof AbstractControlDirective): boolean {
        const control = this.formFieldControl()?.ngControl;
        return !!control && !!control[prop];
    }

    constructor() {
        effect((onCleanup) => {
            const formFieldControl = this.formFieldControl();

            if (formFieldControl) {
                const subscription = formFieldControl.stateChanges.subscribe(() => this.changeDetectorRef.markForCheck());
                onCleanup(() => subscription.unsubscribe());
            }
        });

        effect(() => {
            const formFieldControl = this.formFieldControl();
            if (formFieldControl) {
                const ids: string[] = [];

                const userAriaDescribedBy = formFieldControl.userAriaDescribedBy?.();

                if (userAriaDescribedBy) {
                    ids.push(...userAriaDescribedBy.split(" "));
                }

                if (this.displayedMessages() === "hint") {
                    if (this.hintChildren().length) {
                        ids.push(...this.hintChildren().map((hint) => hint.id()));
                    }
                } else if (this.errorChildren()) {
                    ids.push(...this.errorChildren().map((error) => error.id()));
                }

                this.formFieldControl()?.setDescribedByIds(ids);
            }
        });
    }

    private assertFormFieldControl() {
        if (!this.formFieldControl() && isDevMode()) {
            throw Error("vds-form-field must contain a VdsFormFieldControl.");
        }
    }
}
